import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  EnvelopeSimpleOpen,
  GoogleLogo,
  MicrosoftOutlookLogo,
  ArrowRight,
  Warning,
  X,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import OnboardingWizard from "@/components/OnboardingWizard";
import ConfettiBurst from "@/components/ConfettiBurst";
import { formatDistanceToNow } from "date-fns";

function Stat({ label, value, testid }) {
  return (
    <div className="border border-border p-6 bg-background" data-testid={testid}>
      <div className="section-tag mb-2">{label}</div>
      <div className="font-mono text-3xl sm:text-4xl font-bold">{value}</div>
    </div>
  );
}

const providers = [
  { name: "Outlook", type: "OAuth", status: "live" },
  { name: "Hotmail", type: "OAuth", status: "live" },
  { name: "Yahoo", type: "IMAP", status: "live" },
  { name: "iCloud", type: "IMAP", status: "live" },
  { name: "AOL", type: "IMAP", status: "live" },
  { name: "Zoho", type: "IMAP", status: "live" },
  { name: "Fastmail", type: "IMAP", status: "live" },
  { name: "Proton", type: "IMAP", status: "live" },
  { name: "Gmail", type: "OAuth", status: "soon" },
];

function ProvidersTable() {
  return (
    <div className="border border-border bg-background p-6 mt-4">
      <div className="section-tag mb-4">/ Supported Providers</div>
      <table className="w-full font-mono text-sm border border-border">
        <thead>
          <tr className="border-b border-border bg-secondary">
            <th className="text-left px-4 py-3 font-semibold">Provider</th>
            <th className="text-left px-4 py-3 font-semibold">Connection Type</th>
            <th className="text-left px-4 py-3 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {providers.map((p) => (
            <tr
              key={p.name}
              className={`border-b border-border ${p.status === "soon" ? "opacity-40" : ""}`}
            >
              <td className="px-4 py-3">{p.name}</td>
              <td className="px-4 py-3">{p.type}</td>
              <td className="px-4 py-3">
                {p.status === "live" ? (
                  <span className="text-emerald-600 font-semibold">● LIVE</span>
                ) : (
                  <span className="border border-border px-2 py-0.5 text-xs uppercase tracking-widest">
                    Coming Soon
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();

  const [bulkPass, setBulkPass] = useState(null);
  const getBulkDeletePass = () =>
    api.get("/me/bulk-delete-pass").then((r) => r.data);

  useEffect(() => {
    getBulkDeletePass()
      .then(setBulkPass)
      .catch(() => setBulkPass({ active: false }));
  }, []);

  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [topSenders, setTopSenders] = useState([]);
  const [oauth, setOauth] = useState({
    gmail_enabled: false,
    outlook_enabled: false,
  });

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return "Good morning";
    if (hour >= 12 && hour < 17) return "Good afternoon";
    if (hour >= 17 && hour < 21) return "Good evening";
    return "Good night";
  }, []);

  const [imapProvider, setImapProvider] = useState("");
  const [imapEmail, setImapEmail] = useState("");
  const [imapPassword, setImapPassword] = useState("");
  const [imapLoading, setImapLoading] = useState(false);
  const [imapDialogOpen, setImapDialogOpen] = useState(false);

  const [onboard, setOnboard] = useState(false);
  const [boom, setBoom] = useState(0);

  const imapProviders = [
    { key: "aol", label: "AOL" },
    { key: "yahoo", label: "Yahoo" },
    { key: "att", label: "AT&T / bellsouth.net" },
    { key: "icloud", label: "iCloud" },
    { key: "zoho", label: "Zoho" },
    { key: "fastmail", label: "FastMail" },
    { key: "proton", label: "Proton" },
  ];

  const imapProviderLabel =
    imapProviders.find((p) => p.key === imapProvider)?.label || imapProvider;

  // ⭐ Subscription logic
  const planLimits = {
    individual: 3,
    family: 10,
    business: 25,
  };

  const subscriptionActive = ["individual", "family", "business"].includes(
    user?.plan
  );

  // ⭐ Inbox limit logic: max(plan_limit, active_pass.inbox_count) — mirrors backend
  const planLimit = planLimits[user?.plan] || 0;
  const passInboxCount = bulkPass?.active ? (bulkPass.inbox_count || bulkPass.count || 0) : 0;
  const limit = Math.max(planLimit, passInboxCount);

  // ⭐ Bulk delete access logic
  const bulkDeleteEnabled =
    bulkPass?.active || (subscriptionActive && bulkPass?.ever_purchased);

  const openImapDialog = (provider) => {
    setImapProvider(provider);
    setImapEmail("");
    setImapPassword("");
    setImapDialogOpen(true);
  };

  const normalizeImapKey = (key) => {
    return key
      .toLowerCase()
      .replace(/[^a-z]/g, "") // remove spaces, dashes, underscores
      .replace("mail", "");   // remove "mail"
  };

  const connectImap = async () => {
    if (!imapEmail || !imapPassword) {
      toast.error(`${imapProvider.toUpperCase()} email and password are required`);
      return;
    }
    setImapLoading(true);
    try {
      await api.post(`/accounts/${imapProvider}/connect`, {
        email: imapEmail,
        password: imapPassword,
      });
      toast.success(`${imapProviderLabel} connected`);
      setImapDialogOpen(false);
      reload();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Failed to connect ${imapProvider}`
      );
    } finally {
      setImapLoading(false);
    }
  };

  const reload = async () => {
    const [s, a, t, o] = await Promise.all([
      api.get("/analytics/summary"),
      api.get("/accounts"),
      api.get("/analytics/top-senders"),
      api.get("/oauth/status"),
    ]);

    setSummary(s.data);
    setAccounts(a.data);
    setTopSenders(t.data);
    setOauth(o.data);

    const key = `seen_onboarding_${user?.user_id}`;
    if (user && !localStorage.getItem(key)) setOnboard(true);

    const flagKey = `confetti_100_${user?.user_id}`;
    if (s.data.deleted_emails >= 100 && !localStorage.getItem(flagKey)) {
      localStorage.setItem(flagKey, "1");
      setBoom(Date.now());
      toast.success("Milestone unlocked: 100 emails deleted");
    }
  };

  useEffect(() => {
    reload();
  }, [user?.user_id]);

  const connect = async (provider) => {
    try {
      const path =
        provider === "gmail"
          ? "/oauth/google/authorize"
          : "/oauth/microsoft/authorize";

      const params = { redirect_to: window.location.origin };
      if (provider === "hotmail") {
        params.provider = "hotmail";
        params.tenant_hint = "consumers";
        params.domain_hint = "consumers";
      }
      if (provider === "outlook") {
        params.provider = "outlook";
        params.tenant_hint = "common";
        params.login_hint = user?.email || undefined;
      }

      const { data } = await api.get(path, { params });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start OAuth");
    }
  };

  const disconnect = async (id) => {
    try {
      await api.delete(`/accounts/${id}`);
      toast.success("Disconnected");
      reload();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const closeOnboard = () => {
    setOnboard(false);
    if (user)
      localStorage.setItem(`seen_onboarding_${user.user_id}`, "1");
  };

  const imapHelpLinks = {
    att: {
      url: "https://www.att.com/support/",
      linkText: "AT&T / bellsouth.net: search AT&T support for \"secure mail key\" and use it as the password"
    },
    yahoo: {
      url: "https://help.yahoo.com/kb/generate-app-password",
      linkText: "Yahoo: How to generate an app password"
    },
    icloud: {
      url: "https://support.apple.com/en-us/HT204397",
      linkText: "iCloud: How to generate an app-specific password"
    },
    aol: {
      url: "https://help.aol.com/articles/Create-and-manage-app-passwords",
      linkText: "AOL: How to create an app password"
    },
    zoho: {
      url: "https://www.zoho.com/mail/help/adminconsole/two-factor-authentication.html",
      linkText: "Zoho: App password instructions"
    },
    proton: {
      url: "https://proton.me/support/email-client-configuration",
      linkText: "Proton Mail: IMAP/SMTP setup"
    }
  };


  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <ConfettiBurst trigger={boom} />
      <OnboardingWizard open={onboard} onClose={closeOnboard} />

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
        <div>
          <div className="section-tag mb-2">/ Dashboard</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl">
            {greeting},{" "}
            {user?.name || user?.email?.split("@")[0]}.
          </h1>
        </div>
        <Link to="/inbox">
          <Button
            className="rounded-none btn-brutalist gap-2"
            data-testid="dash-cleanup-btn"
          >
            Clean inbox <ArrowRight size={16} />
          </Button>
        </Link>
      </div>

      {bulkPass?.active && (() => {
        const daysLeft = Math.max(
          0,
          Math.ceil(
            (new Date(bulkPass.expires_at) - new Date()) /
              (1000 * 60 * 60 * 24)
          )
        );
        return (
          <div className="mb-6 p-4 border-2 border-black bg-yellow-200 font-mono text-sm flex items-center justify-between flex-wrap gap-2">
            <span>
              BULK DELETE PASS ACTIVE — EXPIRES{" "}
              {formatDistanceToNow(new Date(bulkPass.expires_at), {
                addSuffix: true,
              }).toUpperCase()}
            </span>
            <span className="border border-black px-2 py-1 bg-white">
              {daysLeft} {daysLeft === 1 ? "DAY" : "DAYS"} LEFT
            </span>
          </div>
        );
      })()}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat
          label="Loaded emails from account"
          value={summary?.total_emails ?? "-"}
          testid="stat-total"
        />
        <Stat
          label="Unread"
          value={summary?.unread_emails ?? "-"}
          testid="stat-unread"
        />
        <Stat
          label="Active subs"
          value={summary?.active_subscriptions ?? "-"}
          testid="stat-subs"
        />
        <Stat
          label="Deleted lifetime"
          value={summary?.deleted_emails ?? "-"}
          testid="stat-deleted"
        />
      </div>

      <div className="border border-border bg-background p-6 mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="section-tag mb-1">Connected inboxes</div>
            <div className="text-xs font-mono text-muted-foreground">
              {accounts.length} of {limit} used ·{" "}
              {user?.plan
                ? `plan: ${user.plan}`
                : bulkPass?.active
                ? `pass: ${passInboxCount} inbox(es), expires ${new Date(bulkPass.expires_at).toLocaleDateString()}`
                : "no plan or active pass"}
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            {/* Gmail */}
            <Button
              size="sm"
              variant="outline"
              className="rounded-none gap-2"
              onClick={() => connect("gmail")}
              disabled={accounts.length >= limit}
            >
              <GoogleLogo size={16} weight="bold" />
              {oauth.gmail_enabled
                ? "(coming Soon)Connect Gmail"
                : "(Coming Soon) Connect Gmail (demo)"}
            </Button>

            {/* Outlook */}
            <Button
              size="sm"
              variant="outline"
              className="rounded-none gap-2"
              onClick={() => connect("outlook")}
              disabled={accounts.length >= limit}
            >
              <MicrosoftOutlookLogo size={16} weight="bold" />
              {oauth.outlook_enabled
                ? "Connect Outlook"
                : "Connect Outlook (demo)"}
            </Button>

            {/* Hotmail */}
            <Button
              size="sm"
              variant="outline"
              className="rounded-none gap-2"
              onClick={() => connect("hotmail")}
              disabled={accounts.length >= limit}
            >
              <MicrosoftOutlookLogo size={16} weight="bold" />
              {oauth.outlook_enabled
                ? "Connect Hotmail"
                : "Connect Hotmail (demo)"}
            </Button>

            {/* IMAP Providers */}
            {imapProviders.map((provider) => (
              <Button
                key={provider.key}
                size="sm"
                variant="outline"
                className="rounded-none gap-2"
                onClick={() => openImapDialog(provider.key)}
                disabled={accounts.length >= limit}
              >
                Connect {provider.label}
              </Button>
            ))}

            <Dialog open={imapDialogOpen} onOpenChange={setImapDialogOpen}>
              <DialogContent className="rounded-none max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display text-xl">
                    Connect {imapProviderLabel}
                  </DialogTitle>
                </DialogHeader>

                {/* Instructions box */}
                <div className="bg-secondary border border-border p-4 text-xs font-mono space-y-2">
                  <p className="font-semibold text-foreground uppercase tracking-widest text-[10px] mb-3">
                    ⚠ App Password Required
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    {imapProviderLabel} requires an <strong className="text-foreground">app-specific password</strong>, not your regular login password.
                  </p>
                  <ol className="space-y-1.5 text-muted-foreground list-none">
                    {[
                      `Sign in to your ${imapProviderLabel} account`,
                      "Go to Security or Account Settings",
                      "Find App Passwords or Generate App Password",
                      "Name it Easy Mail and click Generate",
                      "Copy the password and paste it below",
                    ].map((step, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-primary font-bold shrink-0">{i + 1}.</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                  {imapHelpLinks?.[normalizeImapKey(imapProvider)] && (
                    <a
                      href={imapHelpLinks[normalizeImapKey(imapProvider)].url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 text-primary underline underline-offset-2 hover:opacity-70 transition-opacity"
                    >
                      {imapHelpLinks[normalizeImapKey(imapProvider)].linkText}
                    </a>
                  )}
                </div>

                {/* Inputs */}
                <div className="grid gap-3 py-2">
                  <input
                    className="input rounded-none"
                    type="email"
                    placeholder={`Your ${imapProviderLabel} email address`}
                    value={imapEmail}
                    onChange={(e) => setImapEmail(e.target.value)}
                    disabled={imapLoading}
                  />
                  <input
                    className="input rounded-none"
                    type="password"
                    placeholder="App password (not your regular password)"
                    value={imapPassword}
                    onChange={(e) => setImapPassword(e.target.value)}
                    disabled={imapLoading}
                  />
                </div>

                <DialogFooter className="flex items-center justify-between gap-2">
                  <p className="text-[10px] text-muted-foreground font-mono">
                    Need help?{" "}
                    <a href="mailto:easymailsupport@gmail.com" className="underline">
                      easymailsupport@gmail.com
                    </a>
                  </p>
                  <Button
                    size="sm"
                    className="rounded-none"
                    onClick={connectImap}
                    disabled={accounts.length >= limit || imapLoading}
                  >
                    {imapLoading ? "Connecting..." : `Connect ${imapProviderLabel}`}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {(!oauth.gmail_enabled || !oauth.outlook_enabled) && (
          <div
            className="border border-yellow-500/40 bg-yellow-500/10 p-3 flex items-start gap-2 text-xs mb-4"
            data-testid="oauth-missing-notice"
          >
            <Warning
              size={16}
              className="text-yellow-600 shrink-0 mt-0.5"
            />
            <div>
              <strong>Demo mode</strong> - Real OAuth keys not
              configured.
              {!oauth.gmail_enabled &&
                " Add GOOGLE_CLIENT_ID + GOOGLE_CLIENT_SECRET to backend/.env."}
              {!oauth.outlook_enabled &&
                " Add MS_CLIENT_ID + MS_CLIENT_SECRET + MS_TENANT_ID for Outlook."}
            </div>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {accounts.map((a) => (
          <div
            key={a.account_id}
            className="border border-border p-4 flex items-center gap-3"
            data-testid={`account-${a.provider}`}
          >
            {a.provider === "gmail" ? (
              <GoogleLogo size={22} weight="bold" />
            ) : a.provider === "outlook" ||
              a.provider === "hotmail" ? (
              <MicrosoftOutlookLogo size={22} weight="bold" />
            ) : (
              <img
                src="/easy-mail-logo.png"
                alt="Easy Mail"
                className="h-7 w-auto"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{a.email}</div>
              <div className="text-xs text-muted-foreground font-mono uppercase">
                {a.provider} ·{" "}
                <span
                  className={
                    a.real
                      ? "text-emerald-600 font-semibold"
                      : "text-yellow-600 font-semibold"
                  }
                >
                  {a.real ? "● LIVE" : "● DEMO"}
                </span>{" "}
                · {a.status}
              </div>
            </div>
            <button
              onClick={() => disconnect(a.account_id)}
              className="p-1 hover:bg-secondary"
              aria-label="Disconnect"
            >
              <X size={16} />
            </button>
          </div>
        ))}
        {accounts.length === 0 && (
          <div className="text-sm text-muted-foreground col-span-2 p-4">
            No inboxes yet. Connect one above.
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border bg-background p-6">
          <div className="section-tag mb-4">Top senders flooding you as of whats loaded from your accounts</div>
          <div className="space-y-2">
                        {topSenders.slice(0, 6).map((s) => {
              const max = topSenders[0]?.count || 1;
              const pct = Math.round((s.count / max) * 100);
              return (
                <div key={s.sender_email} className="flex items-center gap-4">
                  <div className="w-40 sm:w-48 truncate text-sm font-medium">
                    {s.sender_name}
                  </div>
                  <div className="flex-1 bg-secondary h-6 relative">
                    <div
                      className="bg-primary h-6"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="font-mono text-sm w-10 text-right">
                    {s.count}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border border-border bg-foreground text-background p-6">
          <div className="section-tag mb-2 opacity-70">Time saved</div>
          <div className="font-mono text-5xl font-bold">
            {summary?.time_saved_minutes ?? "-"}
          </div>
          <div className="text-sm opacity-70 mt-1">minutes back in your life</div>
          <div className="mt-6 text-xs font-mono uppercase tracking-widest opacity-70">
            ~7 sec saved per deletion
          </div>
        </div>
      </div>

      <ProvidersTable />
    </div>
  );
}
